// src/lib/components/atoms/ScheduleSummary/ScheduleSummaryAtom.tsx

import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { RRule } from 'rrule';
import {
  Schedule,
  MonthlyMode,
  YearlyMode,
  Weekday,
  Ordinal,
  DayOfYearValue,
  WeekdayOfMonthValue,
  WeekdayOfYearValue,
} from 'types/schedule';
import './ScheduleSummaryAtom.scss';

const ORDINALS: Record<Ordinal, string> = {
  First: 'first',
  Second: 'second',
  Third: 'third',
  Fourth: 'fourth',
  Last: 'last',
};

const WEEKDAYS: Weekday[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export interface ScheduleSummaryAtomProps {
  /** Either a full Schedule or just `{ rrule: string }` */
  schedule: { rrule: string } | Schedule | Partial<Schedule>;
  /** Optional dtstart override - if provided, will be used instead of dtstart from RRULE */
  dtstart?: Date;
  className?: string;
}

const ScheduleSummaryAtom: React.FC<ScheduleSummaryAtomProps> = ({ 
  schedule = {}, 
  dtstart,
  className = '' 
}) => {
  const { t } = useTranslation();

  const summary = useMemo(() => {
    // Handle empty/null/undefined RRULE case first
    if ('rrule' in schedule) {
      if (!schedule.rrule || schedule.rrule.trim() === '') {
        return t('scheduleEditor.notDefined', { defaultValue: 'Not Defined' });
      }
    } else {
      // No rrule property at all - check if it's a valid Schedule object
      const sched = schedule as Schedule;
      if (!sched.frequency) {
        return t('scheduleEditor.notDefined', { defaultValue: 'Not Defined' });
      }
    }

    // If we have an RRULE, parse it and convert to Schedule format
    if ('rrule' in schedule) {
      let rule: RRule;
      try {
        rule = RRule.fromString(schedule.rrule);
      } catch (error) {
        return t('scheduleEditor.invalidRRule', { defaultValue: 'Invalid Schedule' });
      }

      // Use provided dtstart or fall back to RRULE's dtstart
      const effectiveDtstart = dtstart || rule.options.dtstart;

      // Map RRule frequency enum to our string union
      const freqNames: Record<number, Schedule['frequency']> = {
        [RRule.DAILY]: 'Daily',
        [RRule.WEEKLY]: 'Weekly',
        [RRule.MONTHLY]: 'Monthly',
        [RRule.YEARLY]: 'Yearly',
      };
      const frequency = freqNames[rule.options.freq] ?? 'Daily';

      // Extract interval (e.g., every 2 weeks, every 3 months)
      const interval = rule.options.interval || 1;

      // Extract end conditions
      let scheduleEndCondition = '';
      if (rule.options.until) {
        const endDate = rule.options.until;
        scheduleEndCondition = ` ${t('scheduleEditor.until', { defaultValue: 'until' })} ${endDate.toLocaleDateString()}`;
      } else if (rule.options.count) {
        scheduleEndCondition = ` ${t('scheduleEditor.for', { defaultValue: 'for' })} ${rule.options.count} ${t('scheduleEditor.occurrences', { defaultValue: 'occurrences' })}`;
      }

      // Weekly weekdays - convert RRule weekday format to our format
      const weekdays: Weekday[] = (rule.options.byweekday ?? []).map((wd: any) => {
        const weekdayIndex = typeof wd === 'number' ? wd : wd.weekday;
        return WEEKDAYS[weekdayIndex];
      });

      // Time extraction from dtstart
      let time = '';
      let zone = 'Local time';
      if (effectiveDtstart) {
        const dt = effectiveDtstart;
        const hh = String(dt.getHours()).padStart(2, '0');
        const mm = String(dt.getMinutes()).padStart(2, '0');
        time = `${hh}:${mm}`;
        
        // Extract timezone information
        try {
          // Get timezone abbreviation (e.g., "EDT", "PST")
          const timeZoneName = dt.toLocaleDateString('en-US', { 
            timeZoneName: 'short' 
          }).split(', ')[1];
          
          // Get timezone offset in a readable format
          const offset = dt.getTimezoneOffset();
          const offsetHours = Math.floor(Math.abs(offset) / 60);
          const offsetMinutes = Math.abs(offset) % 60;
          const offsetSign = offset <= 0 ? '+' : '-';
          const offsetString = `UTC${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
          
          // Use the timezone name if available, otherwise fall back to offset
          zone = timeZoneName || offsetString;
        } catch (error) {
          zone = 'Local time';
        }
        
        // Don't show time if it's midnight (00:00) unless explicitly set
        if (time === '00:00' && !dtstart) {
          time = '';
        }
      }

      // Monthly handling
      let monthlyMode: MonthlyMode = 'dayOfMonth';
      let dayOfMonth = 1;
      let weekdayOfMonth: WeekdayOfMonthValue = {
        ordinal: 'First',
        weekdays: [],
      };

      if (frequency === 'Monthly') {
        if (rule.options.bymonthday && rule.options.bymonthday.length > 0) {
          // Monthly by day of month
          dayOfMonth = rule.options.bymonthday[0];
        } else if (rule.options.byweekday && rule.options.byweekday.length > 0) {
          // Monthly by weekday (e.g., first Monday)
          monthlyMode = 'weekdayOfMonth';
          const wd = rule.options.byweekday[0] as any;
          
          if (typeof wd === 'object' && wd && 'n' in wd) {
            // Has ordinal (e.g., first, second, last)
            const ordinalMap: Record<number, Ordinal> = {
              1: 'First',
              2: 'Second', 
              3: 'Third',
              4: 'Fourth',
              [-1]: 'Last'
            };
            weekdayOfMonth = {
              ordinal: ordinalMap[wd.n] || 'First',
              weekdays: [WEEKDAYS[wd.weekday]],
            };
          } else {
            // No ordinal specified - use first occurrence
            const weekdayIndex = typeof wd === 'number' ? wd : wd.weekday;
            weekdayOfMonth = {
              ordinal: 'First',
              weekdays: [WEEKDAYS[weekdayIndex]],
            };
          }
        }
      }

      // Yearly handling
      let yearlyMode: YearlyMode = 'dayOfYear';
      let dayOfYear: DayOfYearValue = {
        month: 'January',
        day: 1,
      };
      let weekdayOfYear: WeekdayOfYearValue = {
        ordinal: 'First',
        weekdays: [],
        month: 'January',
      };

      if (frequency === 'Yearly') {
        const bymonth = rule.options.bymonth ?? [];
        const monthIndex = bymonth.length > 0 ? bymonth[0] - 1 : 0;
        const month = MONTHS[monthIndex];

        if (rule.options.bymonthday && rule.options.bymonthday.length > 0) {
          // Yearly by specific date
          dayOfYear = {
            month,
            day: rule.options.bymonthday[0],
          };
        } else if (rule.options.byweekday && rule.options.byweekday.length > 0) {
          // Yearly by weekday (e.g., first Monday of January)
          yearlyMode = 'weekdayOfYear';
          const wd = rule.options.byweekday[0] as any;
          
          if (typeof wd === 'object' && wd && 'n' in wd) {
            const ordinalMap: Record<number, Ordinal> = {
              1: 'First',
              2: 'Second', 
              3: 'Third',
              4: 'Fourth',
              [-1]: 'Last'
            };
            weekdayOfYear = {
              ordinal: ordinalMap[wd.n] || 'First',
              weekdays: [WEEKDAYS[wd.weekday]],
              month,
            };
          } else {
            const weekdayIndex = typeof wd === 'number' ? wd : wd.weekday;
            weekdayOfYear = {
              ordinal: 'First',
              weekdays: [WEEKDAYS[weekdayIndex]],
              month,
            };
          }
        }
      }

      // Convert to Schedule format for summary generation
      schedule = {
        frequency,
        interval,
        weekdays,
        time,
        zone,
        monthlyMode,
        yearlyMode,
        dayOfMonth,
        weekdayOfMonth,
        dayOfYear,
        weekdayOfYear,
        endCondition: scheduleEndCondition,
      } as Schedule & { endCondition: string };
    }

    // Generate summary from Schedule object
    const {
      frequency,
      interval = 1,
      weekdays = [],
      monthlyMode = 'dayOfMonth' as MonthlyMode,
      yearlyMode = 'dayOfYear' as YearlyMode,
      dayOfMonth = 1,
      weekdayOfMonth,
      dayOfYear,
      weekdayOfYear,
      time,
      zone,
      endCondition = '',
    } = schedule as Schedule & { endCondition?: string };

    // Helper to append time+zone+end condition
    const tzSuffix = () => {
      if (!time || time === '') return endCondition || '';
      let s = ` ${t('scheduleEditor.timeLabel', { defaultValue: 'at' })} ${time}`;
      
      // Always show timezone if we have one and it's not the default
      if (zone && zone !== t('scheduleEditor.timezonePlaceholder', { defaultValue: 'Local time' })) {
        s += ` ${zone}`;
      }
      
      // Add end condition
      s += endCondition || '';
      
      return s;
    };

    // Helper for interval text
    const intervalText = (baseText: string, pluralText?: string) => {
      if (interval === 1) {
        return baseText;
      }
      return `${t('scheduleEditor.every', { defaultValue: 'Every' })} ${interval} ${pluralText || baseText.toLowerCase()}s`;
    };

    // Daily
    if (frequency === 'Daily') {
      const base = intervalText(
        t('scheduleEditor.everyDay', { defaultValue: 'Every day' }),
        t('scheduleEditor.day', { defaultValue: 'day' })
      );
      return `${base}${tzSuffix()}`;
    }

    // Weekly
    if (frequency === 'Weekly') {
      if (weekdays.length === 0) {
        const base = intervalText(
          t('scheduleEditor.everyWeek', { defaultValue: 'Every week' }),
          t('scheduleEditor.week', { defaultValue: 'week' })
        );
        return `${base}${tzSuffix()}`;
      }
      
      const days = weekdays
        .map((d) => t(`scheduleEditor.week.${d.toLowerCase()}`, { defaultValue: d }))
        .join(`, `);
      
      if (interval === 1) {
        return `${t('scheduleEditor.every', { defaultValue: 'Every' })} ${days}${tzSuffix()}`;
      } else {
        return `${t('scheduleEditor.every', { defaultValue: 'Every' })} ${interval} ${t('scheduleEditor.weeks', { defaultValue: 'weeks' })} ${t('scheduleEditor.on', { defaultValue: 'on' })} ${days}${tzSuffix()}`;
      }
    }

    // Monthly
    if (frequency === 'Monthly') {
      let base = '';
      
      if (monthlyMode === 'dayOfMonth' || !weekdayOfMonth?.weekdays?.length) {
        base = intervalText(
          `${t('scheduleEditor.everyMonthOnDay', { defaultValue: 'Every month on day' })} ${dayOfMonth}`,
          `${t('scheduleEditor.month', { defaultValue: 'month' })} ${t('scheduleEditor.onDay', { defaultValue: 'on day' })} ${dayOfMonth}`
        );
      } else {
        const ordLabel = t(`scheduleEditor.ordinal.${ORDINALS[weekdayOfMonth.ordinal]}`, {
          defaultValue: weekdayOfMonth.ordinal,
        });
        const days = weekdayOfMonth.weekdays
          .map((d) => t(`scheduleEditor.week.${d.toLowerCase()}`, { defaultValue: d }))
          .join(` ${t('scheduleEditor.and', { defaultValue: 'and' })} `);
        
        if (interval === 1) {
          base = `${t('scheduleEditor.everyMonthOn', { defaultValue: 'Every month on the' })} ${ordLabel} ${days}`;
        } else {
          base = `${t('scheduleEditor.every', { defaultValue: 'Every' })} ${interval} ${t('scheduleEditor.months', { defaultValue: 'months' })} ${t('scheduleEditor.onThe', { defaultValue: 'on the' })} ${ordLabel} ${days}`;
        }
      }
      
      return `${base}${tzSuffix()}`;
    }

    // Yearly
    if (frequency === 'Yearly') {
      let base = '';
      
      if (yearlyMode === 'dayOfYear' || !weekdayOfYear?.weekdays?.length) {
        base = intervalText(
          `${t('scheduleEditor.everyYearOn', { defaultValue: 'Every year on' })} ${dayOfYear?.month || 'January'} ${dayOfYear?.day || 1}`,
          `${t('scheduleEditor.year', { defaultValue: 'year' })} ${t('scheduleEditor.on', { defaultValue: 'on' })} ${dayOfYear?.month || 'January'} ${dayOfYear?.day || 1}`
        );
      } else {
        const ordLabel = t(`scheduleEditor.ordinal.${ORDINALS[weekdayOfYear.ordinal]}`, {
          defaultValue: weekdayOfYear.ordinal,
        });
        const days = weekdayOfYear.weekdays
          .map((d) => t(`scheduleEditor.week.${d.toLowerCase()}`, { defaultValue: d }))
          .join(` ${t('scheduleEditor.and', { defaultValue: 'and' })} `);
        
        if (interval === 1) {
          base = `${t('scheduleEditor.everyYearOnThe', { defaultValue: 'Every year on the' })} ${ordLabel} ${days} ${t('scheduleEditor.of', { defaultValue: 'of' })} ${weekdayOfYear.month}`;
        } else {
          base = `${t('scheduleEditor.every', { defaultValue: 'Every' })} ${interval} ${t('scheduleEditor.years', { defaultValue: 'years' })} ${t('scheduleEditor.onThe', { defaultValue: 'on the' })} ${ordLabel} ${days} ${t('scheduleEditor.of', { defaultValue: 'of' })} ${weekdayOfYear.month}`;
        }
      }
      
      return `${base}${tzSuffix()}`;
    }

    return t('scheduleEditor.customSchedule', { defaultValue: 'Custom schedule' });
  }, [schedule, dtstart, t]);

  return <span className={`schedule-summary-atom ${className}`}>{summary}</span>;
};

export default ScheduleSummaryAtom;